import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { PropsWithChildren, ReactNode } from 'react';
import { useI18n } from '@/providers/i18n';

type TextEditDialogProps = PropsWithChildren<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  /** Optional footer actions; if omitted, a default Cancel/Save row is rendered */
  footer?: ReactNode;
  /** Called when the default Save button is clicked */
  onSave?: () => void;
  /** Called when the default Cancel button is clicked */
  onCancel?: () => void;
  /** Optional className for the scrollable editor container */
  contentClassName?: string;
}>;

export function TextEditDialog({
  open,
  onOpenChange,
  title = 'Edit Text',
  footer,
  onSave,
  onCancel,
  contentClassName,
  children,
}: TextEditDialogProps) {
  const { t } = useI18n();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden rounded-3xl border-none bg-sidebar/95 backdrop-blur-2xl shadow-2xl transition-all">
        <DialogHeader className="px-6 pt-6 pb-6 flex-shrink-0 bg-transparent">
          <DialogTitle className="text-base font-bold tracking-tight text-foreground/90">{title}</DialogTitle>
        </DialogHeader>
        <div className={
          [
            'px-6 pb-6',
            contentClassName ?? '',
          ].filter(Boolean).join(' ')
        }>
          <div className="rounded-2xl border border-sidebar-border/30 bg-sidebar-accent/30 overflow-hidden h-[400px] flex flex-col">
            <div className="flex-1 overflow-auto custom-scrollbar">
              {children}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-sidebar-border/30 bg-transparent shrink-0">
          {footer ?? (
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" className="rounded-xl h-9 px-4 text-xs font-bold hover:bg-sidebar-accent" onClick={onCancel}>
                {t('Cancel')}
              </Button>
              <Button size="sm" className="rounded-xl h-9 px-6 text-xs font-bold shadow-lg shadow-primary/20 transition-all active:scale-95" onClick={onSave}>
                {t('Save')}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


